import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Box, Container, Typography, Stack, alpha } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import { useDispatch } from '../../redux/store';
import { takeOrder } from '../../redux/features/order/orderSlice';
import { useAuthContext } from '../../auth/useAuthContext';
import { useVoiceAgentSocket, AgentRequestPayload } from '../../hooks/useVoiceAgentSocket';
import { PATH_VOICE_AGENT } from '../../routes/paths';
import Iconify from '../../components/iconify';
import VoiceAgentRequestQueue from '../../sections/voice-agent/VoiceAgentRequestQueue';

export default function VoiceAgentDashboardPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useAuthContext();
  const theme = useTheme();
  const [requests, setRequests] = useState<AgentRequestPayload[]>([]);
  const [isTaking, setIsTaking] = useState(false);
  const [conversationSnapshots, setConversationSnapshots] = useState<Record<string, any[]>>({});

  const handleAgentRequest = useCallback((payload: AgentRequestPayload) => {
    if (payload.type === 'NEW') {
      setRequests(prev => {
        if (prev.some(r => r.orderId === payload.orderId)) return prev;
        return [payload, ...prev];
      });
      toast.warning(`Nouveau client en attente — Commande #${payload.orderId}`, {
        position: 'top-right',
        autoClose: 10000,
        toastId: `agent-req-${payload.orderId}`,
      });
    } else {
      setRequests(prev => prev.filter(r => r.orderId !== payload.orderId));
    }
  }, []);

  const handleConversationSnapshot = useCallback((sid: string, steps: any[]) => {
    setConversationSnapshots(prev => ({ ...prev, [sid]: steps }));
  }, []);

  useVoiceAgentSocket({
    subOrgId: user?.subOrgId,
    onAgentRequest: handleAgentRequest,
    onConversationSnapshot: handleConversationSnapshot,
  });

  const handleTake = useCallback(async (orderId: number) => {
    setIsTaking(true);
    try {
      await dispatch(takeOrder(orderId)).unwrap();
      const req = requests.find(r => r.orderId === orderId);
      const liveSteps = req?.sessionId ? conversationSnapshots[req.sessionId] : undefined;
      setRequests(prev => prev.filter(r => r.orderId !== orderId));
      navigate(PATH_VOICE_AGENT.orderDetail(orderId), { state: { liveSteps } });
    } catch (err: any) {
      if (err === 'ALREADY_TAKEN') {
        toast.error('Cette commande a déjà été prise en charge.');
        setRequests(prev => prev.filter(r => r.orderId !== orderId));
      } else {
        toast.error('Erreur lors de la prise en charge.');
      }
    } finally {
      setIsTaking(false);
    }
  }, [dispatch, navigate, requests, conversationSnapshots]);

  const pendingCount = requests.filter(r => r.type === 'NEW').length;

  return (
    <>
      <Helmet>
        <title>File vocale | Agent Drive-Thru</title>
      </Helmet>

      <Container maxWidth="md" sx={{ py: 3 }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight={800} color="text.primary">
              File d&apos;attente vocale
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Clients nécessitant une intervention humaine
            </Typography>
          </Box>

          {/* Live indicator */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              px: 1.5,
              py: 0.75,
              borderRadius: 2,
              bgcolor: pendingCount > 0
                ? alpha(theme.palette.warning.main, 0.1)
                : alpha(theme.palette.grey[500], 0.06),
              border: `1px solid ${pendingCount > 0
                ? alpha(theme.palette.warning.main, 0.25)
                : alpha(theme.palette.divider, 0.12)}`,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: pendingCount > 0 ? 'warning.main' : 'text.disabled',
                ...(pendingCount > 0 && {
                  animation: 'livePulse 1.4s ease-in-out infinite',
                  '@keyframes livePulse': {
                    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                    '50%': { opacity: 0.4, transform: 'scale(0.7)' },
                  },
                }),
              }}
            />
            <Typography
              variant="caption"
              fontWeight={700}
              color={pendingCount > 0 ? 'warning.main' : 'text.disabled'}
            >
              {pendingCount > 0 ? `${pendingCount} en attente` : 'Aucune demande'}
            </Typography>
          </Stack>
        </Stack>

        <VoiceAgentRequestQueue
          requests={requests}
          onTake={handleTake}
          isLoading={isTaking}
        />
      </Container>
    </>
  );
}
